import { Die, YatzyScores } from '../types/game';

export interface ScoreCategory {
  key: keyof YatzyScores;
  name: string;
  description: string;
  isUpperSection: boolean;
}

export const scoreCategories: ScoreCategory[] = [
  { key: 'ones', name: 'Ettor', description: 'Summan av alla ettor', isUpperSection: true },
  { key: 'twos', name: 'Tvåor', description: 'Summan av alla tvåor', isUpperSection: true },
  { key: 'threes', name: 'Treor', description: 'Summan av alla treor', isUpperSection: true },
  { key: 'fours', name: 'Fyror', description: 'Summan av alla fyror', isUpperSection: true },
  { key: 'fives', name: 'Femmor', description: 'Summan av alla femmor', isUpperSection: true },
  { key: 'sixes', name: 'Sexor', description: 'Summan av alla sexor', isUpperSection: true },
  { key: 'onePair', name: 'Ett par', description: 'Två tärningar med samma värde', isUpperSection: false },
  { key: 'twoPairs', name: 'Två par', description: 'Två olika par', isUpperSection: false },
  { key: 'threeOfAKind', name: 'Triss', description: 'Tre tärningar med samma värde', isUpperSection: false },
  { key: 'fourOfAKind', name: 'Fyrtal', description: 'Fyra tärningar med samma värde', isUpperSection: false },
  { key: 'smallStraight', name: 'Liten stege', description: '1-2-3-4-5', isUpperSection: false },
  { key: 'largeStraight', name: 'Stor stege', description: '2-3-4-5-6', isUpperSection: false },
  { key: 'fullHouse', name: 'Kåk', description: 'Triss + par', isUpperSection: false },
  { key: 'chance', name: 'Chans', description: 'Summan av alla tärningar', isUpperSection: false },
  { key: 'yatzy', name: 'Yatzy', description: 'Fem lika', isUpperSection: false }
];

export const toggleDieLock = (dice: Die[], dieId: number): Die[] => {
  return dice.map(die => 
    die.id === dieId ? { ...die, isLocked: !die.isLocked } : die
  );
};

export const resetDice = (count: 5 | 6 = 5): Die[] => {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    value: 0, // 0 = unknown, shows as ? until first roll
    isLocked: false
  }));
};

export const rollDice = (dice: Die[]): Die[] => {
  return dice.map(die => ({
    ...die,
    value: die.isLocked ? die.value : Math.floor(Math.random() * 6) + 1
  }));
};

const countValues = (dice: Die[]): number[] => {
  const counts = new Array(7).fill(0);
  dice.forEach(die => counts[die.value]++);
  return counts;
};

const getDiceValues = (dice: Die[]): number[] => {
  return dice.map(die => die.value).sort((a, b) => a - b);
};

export const calculatePossibleScore = (dice: Die[], category: keyof YatzyScores): number => {
  const values = getDiceValues(dice);
  const counts = countValues(dice);
  
  switch (category) {
    case 'ones':
    case 'twos':
    case 'threes':
    case 'fours':
    case 'fives':
    case 'sixes':
      const targetValue = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes'].indexOf(category) + 1;
      return counts[targetValue] * targetValue;
    
    case 'onePair':
      for (let i = 6; i >= 1; i--) {
        if (counts[i] >= 2) return i * 2;
      }
      return 0;
    
    case 'twoPairs':
      const pairs: number[] = [];
      for (let i = 6; i >= 1; i--) {
        if (counts[i] >= 2) pairs.push(i);
      }
      return pairs.length >= 2 ? pairs[0] * 2 + pairs[1] * 2 : 0;
    
    case 'threeOfAKind':
      for (let i = 6; i >= 1; i--) {
        if (counts[i] >= 3) return i * 3;
      }
      return 0;
    
    case 'fourOfAKind':
      for (let i = 6; i >= 1; i--) {
        if (counts[i] >= 4) return i * 4;
      }
      return 0;
    
    case 'smallStraight':
      const smallStraight = [1, 2, 3, 4, 5];
      return smallStraight.every(val => counts[val] >= 1) ? 15 : 0;
    
    case 'largeStraight':
      const largeStraight = [2, 3, 4, 5, 6];
      return largeStraight.every(val => counts[val] >= 1) ? 20 : 0;
    
    case 'fullHouse':
      let threeValue = 0;
      let pairValue = 0;
      // Hitta högsta trissen först
      for (let i = 6; i >= 1; i--) {
        if (counts[i] === 3) {
          threeValue = i;
          break;
        }
      }
      // Hitta högsta paret som inte är samma värde som trissen
      for (let i = 6; i >= 1; i--) {
        if (i !== threeValue && counts[i] >= 2) {
          pairValue = i;
          break;
        }
      }
      return threeValue && pairValue ? (threeValue * 3 + pairValue * 2) : 0;
    
    case 'chance':
      return values.reduce((sum, val) => sum + val, 0);
    
    case 'yatzy':
      for (let i = 1; i <= 6; i++) {
        if (counts[i] >= 5) return 50;
      }
      return 0;
    
    default:
      return 0;
  }
};

export const calculateTotalScore = (scores: YatzyScores): { total: number; upperSection: number; bonus: number } => {
  const upperSectionCategories: (keyof YatzyScores)[] = ['ones', 'twos', 'threes', 'fours', 'fives', 'sixes'];
  
  const upperSection = upperSectionCategories.reduce((sum, category) => {
    return sum + (scores[category] || 0);
  }, 0);
  
  const bonus = upperSection >= 63 ? 50 : 0;
  
  const lowerSection = Object.entries(scores).reduce((sum, [key, value]) => {
    if (!upperSectionCategories.includes(key as keyof YatzyScores)) {
      return sum + (value || 0);
    }
    return sum;
  }, 0);
  
  return {
    total: upperSection + bonus + lowerSection,
    upperSection,
    bonus
  };
};